import { PanoControls, RayCaster } from '../controls';
import { SceneCoordinate } from '../projections';
import { setPointScale } from '../utilities';
import { debounce } from 'throttle-debounce';
import { CameraImage } from '../cameras/cameras';
import {
  Mesh,
  Scene,
  SphereGeometry,
  MeshBasicMaterial,
  Vector3
} from 'three';

class ImageObservation extends Mesh {

  constructor(geometry, material, row) {
    super(geometry, material);
    this.name = "image_" + row.toString();
  }
}

class PointObservation extends Mesh {

  constructor(geometry, material, row) {
    super(geometry, material);
    this.name = "point_" + row.toString();
  }
}

export const pointObsColor = 0xe6e600;
export const imageObsColor = 0x9900cc; 

export class CameraAlignerAdv {
  private controls;
  private setAddObservations;
  private setViewerTooltipText;
  private resetObservationType;
  private currentRow=-1;
  private observationType="none";
  public enabled = false;
  public scene: Scene;
  private sceneObs=[];
  private enabled3dMode=false;
  private pointIdxAdd = 0;
  private imageIdxAdd = 0;

  private sphereGeometry = new SphereGeometry(0.05, 20, 20);

  constructor(controls, scene, props) {
    const {setAddObservations, setViewerTooltipText, resetObservationType} = props;

    this.scene = scene;
    this.controls = controls;
    this.setAddObservations = setAddObservations;
    this.setViewerTooltipText = setViewerTooltipText;
    this.resetObservationType = resetObservationType;
    this.enabled3dMode = this.controls.orbit.status;
  }

  get numEntries() {
    return this.sceneObs.length;
  }

  get viewer() {
    return this.controls.viewer;
  }

  get selecting() {
    const type = this.observationType;
    return ((type === "point") || (type === "image"));
  }

  setState(state) {
    this.enabled = state;
  }

  update3dMode() {
    this.enabled3dMode = this.controls.orbit.status;
  }

  reset() {
    for (let observation of this.sceneObs) {
      this.scene.remove(observation.imageMesh);
      this.scene.remove(observation.pointMesh);
    }
    this.sceneObs = [];
    this.pointIdxAdd = 0;
    this.imageIdxAdd = 0;
  }

  addRow() {
    this.sceneObs.push(this.emptyObservation());
  }

  setErrors(errors) {
    for (let i = 0; i<this.sceneObs.length; i++) {
      this.sceneObs[i].error = errors[i];
    }
  }

  getError(i) {
    return this.sceneObs[i].error;
  }

  deleteRow(i) {
    let imageName = this.getMeshName("image", this.sceneObs[i].imageIdx);
    let pointName = this.getMeshName("point", this.sceneObs[i].pointIdx);
    this.removeMarker(imageName);
    this.removeMarker(pointName);
    this.sceneObs.splice(i, 1);
  }

  getImageIdx(){
    let idx = this.imageIdxAdd;
    this.imageIdxAdd++;
    return idx;
  }

  getPointIdx(){
    let idx = this.pointIdxAdd;
    this.pointIdxAdd++;
    return idx;
  }

  getMeshName(type, i) {
    let name = type + "_" + i.toString();
    return name;
  }

  getImageObs(i) {
    return this.sceneObs[i].imageObs;
  }

  getPointObs(i) {
    return this.sceneObs[i].pointObs;
  }

  getCamPose(i) {
    return this.sceneObs[i].pose;
  }

  getCamId(i) {
    return this.sceneObs[i].imageIdentifier;
  }

  getCamName(i){
    return this.sceneObs[i].name;
  }

  setObservationType(type) {
    this.observationType = type;
  }

  setCurrentRow(rowNum) {
    this.currentRow = rowNum;
  }

  clickImage(event) {
    // Get direction vector
    if (this.viewer.cameraList.current != null && !this.enabled3dMode) {

      let raycaster = new RayCaster(this.viewer, event);
      let direction = raycaster.vectorFromEvent(event);

      let i = this.currentRow;
      let position = this.viewer.cameraList.current.position.value;
      let rotation = this.viewer.cameraList.current.rotation.array;

      //Allows for observations to overwrite each other
      this.removeMarker(this.getMeshName("image", this.sceneObs[i].imageIdx));

      this.sceneObs[i].name = this.viewer.cameraList.current.name;
      this.sceneObs[i].imageIdentifier = this.viewer.cameraList.current.id;
      this.sceneObs[i].imageObs = direction;
      this.sceneObs[i].imageIdx = this.getImageIdx();

      this.sceneObs[i].pose = [this.viewer.cameraList.current.path,
        position.x, position.y, position.z,
        rotation[0], rotation[1], rotation[2]];

      let imageDir = direction.clone().add(this.viewer.camera.position);

      this.addImageObservationMesh(imageDir);
      this.setAddObservations(true);
    }
  }

  clickScene(event) {
    let coordinate = this.controls.getPickerResults(event);
    if (coordinate === null) {
      console.warn("Cloud position: no result");
      this.setAddObservations(false);
      return;
    }

    let pos = new SceneCoordinate(coordinate).toDataProjection();
    let i = this.currentRow;

    //Allows for observations to overwrite each other
    this.removeMarker(this.getMeshName("point", this.sceneObs[i].pointIdx));

    this.sceneObs[i].pointObs = pos;
    this.sceneObs[i].pointIdx = this.getPointIdx();

    this.addPointObservationMesh(coordinate);
    this.setAddObservations(true);
  }

  mouseClick(event) {
    let observationToAdd = this.observationType;

    if (observationToAdd === "point") {
      this.clickScene(event);
    } else if (observationToAdd === "image") {
      this.clickImage(event);
    } else {
      console.warn("No observation type selected");
      this.setAddObservations(false);
    }
    this.setAddObservations(false);
  }

  cancelAlignment() {
    this.unhideObservations();
    this.resetObservationType();
  }

  updateSelectionText() {
    const type = this.observationType;

    if ((type === "point") || (type === "image")) {
      const text = `Click to add ${type} observation.<br>Right-click to cancel`;
      this.setViewerTooltipText(text);
    } else {
      this.setViewerTooltipText("");
    }
  }

  addPointObservationMesh(position) {
    // if mesh doesnt exist (check whatever array you add this to)

    let sphereMaterial = new MeshBasicMaterial({color: pointObsColor});
    let mesh = new PointObservation(this.sphereGeometry, sphereMaterial,
      this.sceneObs[this.currentRow].pointIdx);
    
    this.scene.add(mesh);
    this.sceneObs[this.currentRow].pointMesh = mesh;

    // update position (new or existing mesh)
    mesh.position.copy(position);
    this.setPointScale(mesh);
  }

  addImageObservationMesh(position) {
    // if mesh doesnt exist (check whatever array you add this to)
    let sphereMaterial = new MeshBasicMaterial({color: imageObsColor});
    let mesh = new ImageObservation(this.sphereGeometry, sphereMaterial,
      this.sceneObs[this.currentRow].imageIdx);
    
    this.scene.add(mesh);
    this.sceneObs[this.currentRow].imageMesh = mesh;

    // update position (new or existing mesh)
    mesh.position.copy(position);
    this.setPointScale(mesh);
  }

  flashObservations(rowNum) {
    const colorImage = this.sceneObs[rowNum].imageMesh?.material.color;
    const colorPoint = this.sceneObs[rowNum].pointMesh?.material.color;
    const flashObservation = (colorObject) => {
      const originalColor = colorObject.getHex();
      const flashColor = 0xffffff;

      let numberOfFlashes = 0;
      const maxNumberOfFlashes = 6;
      const timerDelay = 500;

      const interval = setInterval(() => {
        if (colorObject.getHex() === originalColor) {
          colorObject.setHex(flashColor);
        } else {
          colorObject.setHex(originalColor);
        }

        numberOfFlashes++;

        if (numberOfFlashes === maxNumberOfFlashes) {
          clearInterval(interval);
        }
      }, timerDelay);
    };

    if (colorImage) {
      flashObservation(colorImage);
    }

    if (colorPoint) {
      flashObservation(colorPoint);
    }
  };

  hideCurrentObservation(type, rowNum) {
    const mesh = type === 'image' ? this.sceneObs[rowNum].imageMesh : this.sceneObs[rowNum].pointMesh;

    if (mesh) {
      mesh.visible = false;
    } 
  }

  setAllObservationsVisible() {
    for (const element of this.sceneObs) {
      const imageMesh = element.imageMesh;
      const pointMesh = element.pointMesh;

      if (imageMesh) {
        imageMesh.visible = true;
      }

      if (pointMesh) {
        pointMesh.visible = true;
      }
    }
  }

  getObservationPosition(rowNum) {
    return this.sceneObs[rowNum].imageObs;
  }

  unhideObservations() {
    const imageMesh = this.sceneObs[this.currentRow].imageMesh;
    const pointMesh = this.sceneObs[this.currentRow].pointMesh;

    if (imageMesh) {
      imageMesh.visible = true;
    }

    if (pointMesh) {
      pointMesh.visible = true;
    }
  }

  emptyObservation = () => {
    return {
      imageObs: null,
      pointObs: null,
      imageMesh: null,
      pointMesh: null,
      imageIdentifier: "",
      name: "No File",
      pose: [],
      error: 0,
      pointIdx: -1,
      imageIdx: -1
    };
  }

  removeMarker(name) {
    this.scene.children.forEach(child => {
      if (child.name === name){
        this.scene.remove(child);
      }
    });
  }

  redrawImageObsMarkers() {
    if (this.sceneObs === null || this.sceneObs.length === 0) {
      return;
    }

    for (let i=0; i<this.sceneObs.length; i++){
      if (this.sceneObs[i].imageObs === null) {
        continue;
      }
      this.removeMarker(this.getMeshName("image", this.sceneObs[i].imageIdx));
      const current = this.viewer.cameraList.getByID(this.sceneObs[i].imageIdentifier);

      if (current === null){
        continue;
      }
      let sphereMaterial = new MeshBasicMaterial({color: 0x9900cc});
      let direction = this.sceneObs[i].imageObs;
      let mesh = new ImageObservation(this.sphereGeometry,
        sphereMaterial, this.sceneObs[i].imageIdx);

      const position = current.position.viewer;
      const rotations = current.rotation;

      let meshPos = new Vector3()
        .copy(direction)
        .applyEuler(rotations.eulerCsvInvert)
        .applyEuler(rotations.euler)
        .add(position);

      mesh.position.copy(meshPos);

      this.scene.add(mesh);
      this.sceneObs[i].imageMesh = mesh;
      this.setPointScale(mesh);

    }
  }

  setPointScale(mesh) {
    setPointScale(this.viewer, mesh, 1.0);
  }

  update(changeDetector) {
    this.update3dMode();
    if (changeDetector === null) {
      return;
    }

    const update = changeDetector.changed;
    if (!update) {
      return;
    }

    this.sceneObs.forEach(observation => {
      // Don't run if data aligner isn't open
      if (!this.enabled){
        return;
      }

      // Resize point observations
      if (observation.pointMesh != null) {
        this.setPointScale(observation.pointMesh);
      }

      // Don't continue if no cameras are loaded
      if (!this.viewer.cameraList.current){
        return;
      }

      if (observation.imageMesh != null){
        const currentID = this.viewer.cameraList.current.id;
        if ((observation.imageIdentifier === currentID)  && !this.enabled3dMode) {
          observation.imageMesh.visible = true;
          this.setPointScale(observation.imageMesh);
        } else {
          observation.imageMesh.visible = false;
        }
      }
    });
  }
}

export class CameraAlignerBasic {
  public enabled = false;
  private increment = 0.10;
  private cameraAngles = [0, 0, 0];
  private controls: PanoControls
  private setSingleAlignment;

  constructor(controls: PanoControls, props) {
    const {setSingleAlignment} = props;

    this.controls = controls;

    // Need to debounce to update ui smoothly
    this.setSingleAlignment = debounce(10, (value) => {
      setSingleAlignment(value);
    });

    this.reset();
  }

  get currentCamera() : CameraImage {
    return this.controls.cameraList?.current;
  }

  setState(state) {
    this.enabled = state;
  }

  reset(values = [0,0,0]) {
    this.cameraAngles = [...values];
    this.updateAlignment();
  }

  onKeyDown(event) {
    if (!this.enabled) {
      return;
    }

    if (event.code === "Numpad7") {
      this.cameraAngles[0] -= this.increment;
    } else if (event.code === "Numpad9") {
      this.cameraAngles[0] += this.increment;
    } else if (event.code === "Numpad4") {
      this.cameraAngles[1] -= this.increment;
    } else if (event.code === "Numpad6") {
      this.cameraAngles[1] += this.increment;
    } else if (event.code === "Numpad1") {
      this.cameraAngles[2] -= this.increment;
    } else if (event.code === "Numpad3") {
      this.cameraAngles[2] += this.increment;
    }

    this.updateAlignment();
  }

  updateAlignment() {
    this.setSingleAlignment([...this.cameraAngles]);
    this.currentCamera?.applyTempRotation(this.cameraAngles);
  }
}